import * as pc from "playcanvas";
import type { GameMap } from "../../sim/gamemap";
import type { Faction, Role } from "../../data/types";

// PlayCanvas scene primitives for the 100%-PlayCanvas TORRENT renderer.
// EVERY entity role is a procedural, zero-asset low-poly mesh built in-engine —
// boxes / prisms / cylinders / cones / spheres, flat-ish metal materials with
// gunmetal hulls and teal / red faction emissive accents (amber for hazards).
// The `art.ts` entityHeight table is the sizing reference so silhouettes read at
// the same relative heights the retired 2D oblique renderer used. All geometry
// is authored in sim-world units so it lines up 1:1 with the 3D Camera.
//
// GLB SEAM: buildTemplate() is the procedural factory. ModelManager checks its
// GLB registry FIRST and only falls back here, so dropping a baked per-role GLB
// later needs no game-code change (see models.ts).

// Vertical exaggeration of the 0..1 heightfield into world-Y. Kept gentle so
// hills read through shading + shadows without occluding units under the tilt.
export const HEIGHT_SCALE = 15;

interface Style { hull: string; dark: string; accent: string; glass: string; accentI: number; }
const STYLE: Record<"vantage" | "feral", Style> = {
  vantage: { hull: "#aab8c4", dark: "#525f6b", accent: "#35e0ff", glass: "#bff4ff", accentI: 3.0 },
  feral: { hull: "#a2734a", dark: "#4a2f1c", accent: "#ff5a3c", glass: "#ffd0a0", accentI: 2.4 },
};
const AMBER = "#ffb020"; // hazard / overflow accent (bombs)

function col(hex: string, s = 1): pc.Color {
  const h = hex.replace("#", "");
  return new pc.Color(
    (parseInt(h.slice(0, 2), 16) / 255) * s,
    (parseInt(h.slice(2, 4), 16) / 255) * s,
    (parseInt(h.slice(4, 6), 16) / 255) * s,
  );
}

function pbr(diffuse: pc.Color, metalness: number, gloss: number): pc.StandardMaterial {
  const m = new pc.StandardMaterial();
  m.diffuse = diffuse;
  m.useMetalness = true;
  m.metalness = metalness;
  m.gloss = gloss;
  m.update();
  return m;
}

function emissiveMat(c: pc.Color, intensity: number): pc.StandardMaterial {
  const m = new pc.StandardMaterial();
  m.diffuse = col("#0c1014");
  m.useMetalness = true;
  m.metalness = 0;
  m.gloss = 0.5;
  m.emissive = c;
  m.emissiveIntensity = intensity;
  m.update();
  return m;
}

export interface FactionMats {
  hull: pc.StandardMaterial;
  dark: pc.StandardMaterial;
  accent: pc.StandardMaterial;
  glass: pc.StandardMaterial;
}

export class SceneAssets {
  readonly app: pc.AppBase;
  readonly device: pc.GraphicsDevice;
  readonly gmap: GameMap;
  readonly mats: Record<"vantage" | "feral", FactionMats>;
  readonly bark: pc.StandardMaterial;
  readonly leaf: pc.StandardMaterial;
  readonly rock: pc.StandardMaterial;
  readonly wreck: pc.StandardMaterial;
  readonly amber: pc.StandardMaterial;
  readonly blob: pc.StandardMaterial;

  constructor(app: pc.AppBase, gmap: GameMap) {
    this.app = app;
    this.device = app.graphicsDevice;
    this.gmap = gmap;
    const mk = (f: "vantage" | "feral"): FactionMats => {
      const s = STYLE[f];
      return {
        hull: pbr(col(s.hull), 0.78, 0.4),
        dark: pbr(col(s.dark), 0.72, 0.5),
        accent: emissiveMat(col(s.accent), s.accentI),
        glass: emissiveMat(col(s.glass), 1.4),
      };
    };
    this.mats = { vantage: mk("vantage"), feral: mk("feral") };
    this.bark = pbr(col("#2c1f13"), 0, 0.3);
    this.leaf = pbr(col("#2f5c37"), 0, 0.35);
    this.rock = pbr(col("#5c626b"), 0.1, 0.35);
    this.wreck = pbr(col("#2a2622"), 0.55, 0.2);
    this.amber = emissiveMat(col(AMBER), 3.2);
    // Cheap blob shadow: an unlit dark disc laid just above the ground under
    // each mobile unit (units cast NO real shadows — only the few static
    // buildings/features do — which keeps the shadow pass tiny at 200+ units).
    const b = new pc.StandardMaterial();
    b.useLighting = false;
    b.diffuse = new pc.Color(0, 0, 0);
    b.opacity = 0.34;
    b.blendType = pc.BLEND_NORMAL;
    b.depthWrite = false;
    b.update();
    this.blob = b;
  }

  // ---- Terrain -------------------------------------------------------------
  /** Lit ground mesh from the heightfield: near-black, vertex-shaded for
   *  elevation/slope, with darkened metal-spot craters and a subtle tiled teal
   *  grid (emissive) baked across it. Receives shadows; casts none. */
  buildTerrain(parent: pc.Entity): pc.Entity {
    const m = this.gmap;
    const N = Math.max(m.cols, 64);
    const W = m.worldW;
    const H = m.worldH;
    const verts = N + 1;
    const positions: number[] = [];
    const colors: number[] = [];
    const uvs: number[] = [];
    const indices: number[] = [];
    const spots = m.def.metalSpots;
    const cs = m.cellSize;

    const lo = col("#0a0f12"); // near-black low ground
    const hi = col("#161d21"); // faintly lifted high ground
    for (let j = 0; j <= N; j++) {
      for (let i = 0; i <= N; i++) {
        const wx = (i / N) * W;
        const wy = (j / N) * H;
        const e = m.elevationAt(wx, wy);
        let crater = 0;
        for (let s = 0; s < spots.length; s++) {
          const d = Math.hypot(wx - spots[s].x, wy - spots[s].y);
          if (d < 18) crater = Math.max(crater, 1 - d / 18);
        }
        const y = e * HEIGHT_SCALE - crater * 1.8;
        positions.push(wx, y, wy);
        const t = Math.min(1, e * 1.2);
        const n = 0.9 + 0.1 * Math.sin(wx * 0.05 + wy * 0.03);
        let r = (lo.r + (hi.r - lo.r) * t) * n;
        let g = (lo.g + (hi.g - lo.g) * t) * n;
        let b = (lo.b + (hi.b - lo.b) * t) * n;
        if (crater > 0) { const k = 1 - crater * 0.6; r *= k; g *= k * 0.9; b *= k * 0.7; }
        colors.push(r, g, b, 1);
        uvs.push(wx / cs, wy / cs); // one grid tile per sim cell
      }
    }
    for (let j = 0; j < N; j++) {
      for (let i = 0; i < N; i++) {
        const a = j * verts + i;
        const b = a + 1;
        const c = a + verts;
        const d = c + 1;
        indices.push(a, c, b, b, c, d);
      }
    }
    const normals = pc.calculateNormals(positions, indices);
    const mesh = new pc.Mesh(this.device);
    mesh.setPositions(positions);
    mesh.setNormals(normals);
    mesh.setColors(colors, 4);
    mesh.setUvs(0, uvs);
    mesh.setIndices(indices);
    mesh.update();

    const mat = new pc.StandardMaterial();
    mat.diffuse = new pc.Color(1, 1, 1);
    mat.diffuseVertexColor = true;
    mat.useMetalness = true;
    mat.metalness = 0.02;
    mat.gloss = 0.12;
    mat.emissive = col("#35e0ff");
    mat.emissiveIntensity = 0.5;
    mat.emissiveMap = this.gridTexture();
    mat.update();

    const mi = new pc.MeshInstance(mesh, mat);
    mi.castShadow = false;
    const e = new pc.Entity("terrain");
    e.addComponent("render", { meshInstances: [mi], castShadows: false, receiveShadows: true });
    parent.addChild(e);
    return e;
  }

  /** A tiny tiled grid tile (near-black cell with faint edge lines). */
  private gridTexture(): pc.Texture {
    const S = 64;
    const cv = document.createElement("canvas");
    cv.width = cv.height = S;
    const g = cv.getContext("2d")!;
    g.fillStyle = "#000000";
    g.fillRect(0, 0, S, S);
    g.strokeStyle = "rgba(120,200,235,0.5)";
    g.lineWidth = 1.5;
    g.beginPath();
    g.moveTo(0.5, 0); g.lineTo(0.5, S);
    g.moveTo(0, 0.5); g.lineTo(S, 0.5);
    g.stroke();
    const tex = new pc.Texture(this.device, {
      width: S, height: S, format: pc.PIXELFORMAT_R8_G8_B8_A8,
      addressU: pc.ADDRESS_REPEAT, addressV: pc.ADDRESS_REPEAT,
      mipmaps: true, minFilter: pc.FILTER_LINEAR_MIPMAP_LINEAR, magFilter: pc.FILTER_LINEAR,
    });
    tex.setSource(cv);
    return tex;
  }

  /** Elevation (world-Y) at a world (x, y) — units/buildings sit on this. */
  elevationY(wx: number, wy: number): number {
    return this.gmap.elevationAt(wx, wy) * HEIGHT_SCALE;
  }

  // ---- Primitive helpers ----------------------------------------------------
  private box(parent: pc.Entity, mat: pc.Material, sx: number, sy: number, sz: number, px = 0, py = 0, pz = 0, castShadow = true): void {
    const mesh = pc.Mesh.fromGeometry(this.device, new pc.BoxGeometry({ halfExtents: new pc.Vec3(sx / 2, sy / 2, sz / 2) }));
    const mi = new pc.MeshInstance(mesh, mat);
    mi.castShadow = castShadow;
    const c = new pc.Entity();
    c.addComponent("render", { meshInstances: [mi], castShadows: castShadow, receiveShadows: true });
    c.setLocalPosition(px, py, pz);
    parent.addChild(c);
  }
  private cyl(parent: pc.Entity, mat: pc.Material, radius: number, height: number, px = 0, py = 0, pz = 0, rx = 0, rz = 0, castShadow = true): void {
    const mesh = pc.Mesh.fromGeometry(this.device, new pc.CylinderGeometry({ radius, height }));
    const mi = new pc.MeshInstance(mesh, mat);
    mi.castShadow = castShadow;
    const c = new pc.Entity();
    c.addComponent("render", { meshInstances: [mi], castShadows: castShadow, receiveShadows: true });
    c.setLocalPosition(px, py, pz);
    if (rx || rz) c.setLocalEulerAngles(rx, 0, rz);
    parent.addChild(c);
  }
  private cone(parent: pc.Entity, mat: pc.Material, baseRadius: number, peakRadius: number, height: number, px = 0, py = 0, pz = 0, castShadow = true): void {
    const mesh = pc.Mesh.fromGeometry(this.device, new pc.ConeGeometry({ baseRadius, peakRadius, height }));
    const mi = new pc.MeshInstance(mesh, mat);
    mi.castShadow = castShadow;
    const c = new pc.Entity();
    c.addComponent("render", { meshInstances: [mi], castShadows: castShadow, receiveShadows: true });
    c.setLocalPosition(px, py, pz);
    parent.addChild(c);
  }
  private sphere(parent: pc.Entity, mat: pc.Material, radius: number, px = 0, py = 0, pz = 0, castShadow = true): void {
    const mesh = pc.Mesh.fromGeometry(this.device, new pc.SphereGeometry({ radius }));
    const mi = new pc.MeshInstance(mesh, mat);
    mi.castShadow = castShadow;
    const c = new pc.Entity();
    c.addComponent("render", { meshInstances: [mi], castShadows: castShadow, receiveShadows: true });
    c.setLocalPosition(px, py, pz);
    parent.addChild(c);
  }
  /** Flat dark disc blob shadow under a mobile unit (unlit, no shadow pass). */
  private blobShadow(parent: pc.Entity, r: number): void {
    const mesh = pc.Mesh.fromGeometry(this.device, new pc.CylinderGeometry({ radius: r, height: 0.2 }));
    const mi = new pc.MeshInstance(mesh, this.blob);
    mi.castShadow = false;
    const c = new pc.Entity();
    c.addComponent("render", { meshInstances: [mi], castShadows: false, receiveShadows: false });
    c.setLocalPosition(0, 0.25, 0);
    parent.addChild(c);
  }

  // ---- Templates (procedural, cloned per live entity by the pool) ----------
  /** Build a template Entity for any drawable def (unit / building / feature). */
  buildTemplate(role: Role, faction: Faction, r: number): pc.Entity {
    const root = new pc.Entity(`tpl_${faction}_${role}`);
    const fac = faction === "feral" ? "feral" : "vantage";
    const M = this.mats[fac];
    switch (role) {
      // --- Buildings ---
      case "factory":
        this.box(root, M.dark, r * 1.9, r * 0.5, r * 1.9, 0, r * 0.25, 0);
        this.box(root, M.hull, r * 1.7, r * 0.9, r * 1.7, 0, r * 0.7, 0);
        this.box(root, M.accent, r * 1.5, r * 0.14, r * 0.4, 0, r * 1.15, -r * 0.6);
        this.box(root, M.glass, r * 0.9, r * 0.5, r * 0.2, 0, r * 0.5, r * 0.95);
        break;
      case "extractor":
        this.cyl(root, M.hull, r * 0.9, r * 0.7, 0, r * 0.35, 0);
        this.cone(root, M.dark, r * 0.5, r * 0.08, r * 1.1, 0, r * 1.0, 0);
        this.cyl(root, M.accent, r * 0.3, r * 0.2, 0, r * 0.7, 0);
        break;
      case "solar":
        this.box(root, M.dark, r * 0.25, r * 0.7, r * 0.25, 0, r * 0.35, 0);
        this.box(root, M.accent, r * 1.7, r * 0.1, r * 1.5, 0, r * 0.8, 0);
        this.box(root, M.glass, r * 1.5, r * 0.12, r * 0.16, 0, r * 0.86, 0);
        break;
      case "wind":
        this.cyl(root, M.dark, r * 0.16, r * 1.6, 0, r * 0.8, 0);
        this.box(root, M.hull, r * 1.6, r * 0.12, r * 0.24, 0, r * 1.5, 0);
        this.box(root, M.hull, r * 0.24, r * 0.12, r * 1.6, 0, r * 1.5, 0);
        this.cyl(root, M.accent, r * 0.2, r * 0.24, 0, r * 1.5, 0, 90, 0);
        break;
      case "converter":
        this.cyl(root, M.hull, r * 0.85, r * 1.0, 0, r * 0.5, 0);
        this.cyl(root, M.accent, r * 0.55, r * 0.2, 0, r * 1.02, 0);
        this.cyl(root, M.glass, r * 0.22, r * 0.3, 0, r * 1.1, 0);
        break;
      case "storage":
        this.cyl(root, M.hull, r * 0.9, r * 1.4, 0, r * 0.7, 0);
        this.cyl(root, M.accent, r * 0.92, r * 0.18, 0, r * 0.7, 0);
        break;
      case "turret":
        this.cyl(root, M.dark, r * 0.95, r * 0.4, 0, r * 0.2, 0);
        this.box(root, M.hull, r * 0.9, r * 0.7, r * 0.9, 0, r * 0.6, 0);
        this.box(root, M.dark, r * 0.24, r * 0.24, r * 1.5, 0, r * 0.7, r * 0.6);
        this.cyl(root, M.accent, r * 0.2, r * 0.16, 0, r * 0.7, 0);
        break;
      // --- Neutral features ---
      case "tree":
        this.cyl(root, this.bark, r * 0.16, r * 0.9, 0, r * 0.45, 0);
        this.cone(root, this.leaf, r * 0.85, r * 0.05, r * 1.5, 0, r * 1.4, 0);
        this.cone(root, this.leaf, r * 0.6, r * 0.05, r * 1.0, 0, r * 2.0, 0);
        break;
      case "rock":
        this.box(root, this.rock, r * 1.5, r * 0.9, r * 1.2, 0, r * 0.4, 0);
        this.box(root, this.rock, r * 0.8, r * 0.7, r * 1.4, r * 0.4, r * 0.7, -r * 0.2);
        break;
      // --- Mobile units (blob-shadowed, no real shadow cast) ---
      case "commander":
        this.blobShadow(root, r * 1.0);
        this.box(root, M.dark, r * 0.8, r * 0.5, r * 1.7, 0, r * 0.5, 0, false);           // hips
        this.box(root, M.hull, r * 1.0, r * 1.1, r * 1.0, 0, r * 1.3, 0, false);           // torso
        this.box(root, M.dark, r * 1.7, r * 0.5, r * 0.5, 0, r * 1.7, 0, false);           // shoulder pods
        this.sphere(root, M.accent, r * 0.32, r * 0.55, r * 1.75, 0, false);               // head/eye
        this.box(root, M.dark, r * 0.28, r * 0.28, r * 1.5, r * 0.5, r * 1.5, r * 0.4, false); // gun
        break;
      case "tank":
        this.blobShadow(root, r * 0.95);
        this.box(root, M.dark, r * 2.0, r * 0.35, r * 0.5, 0, r * 0.25, -r * 0.6, false);   // tracks
        this.box(root, M.dark, r * 2.0, r * 0.35, r * 0.5, 0, r * 0.25, r * 0.6, false);
        this.box(root, M.hull, r * 1.7, r * 0.5, r * 1.2, 0, r * 0.6, 0, false);            // hull
        this.cyl(root, M.hull, r * 0.5, r * 0.4, 0, r * 0.95, 0, 0, 0, false);              // turret
        this.box(root, M.dark, r * 0.22, r * 0.22, r * 1.4, 0, r * 0.95, r * 0.7, false);   // barrel
        break;
      case "assault":
        this.blobShadow(root, r * 0.9);
        this.box(root, M.hull, r * 1.9, r * 0.9, r * 1.7, 0, r * 0.6, 0, false);
        this.sphere(root, M.accent, r * 0.3, -r * 0.3, r * 0.7, 0, false);
        this.box(root, M.dark, r * 0.34, r * 0.34, r * 1.6, 0, r * 0.7, r * 0.5, false);
        break;
      case "constructor":
        this.blobShadow(root, r * 0.85);
        this.box(root, M.hull, r * 1.5, r * 0.7, r * 1.3, 0, r * 0.45, 0, false);
        this.cyl(root, M.dark, r * 0.32, r * 0.4, -r * 0.1, r * 0.9, 0, 0, 0, false);
        this.box(root, M.accent, r * 0.16, r * 0.16, r * 1.3, r * 0.5, r * 0.9, r * 0.5, false); // nanolathe arm
        break;
      case "scout":
        this.blobShadow(root, r * 0.7);
        this.cone(root, M.hull, r * 0.7, r * 0.05, r * 1.6, 0, r * 0.4, 0, false); // wedge nose (points +Z)
        this.sphere(root, M.accent, r * 0.22, 0, r * 0.45, -r * 0.2, false);
        break;
      case "raider":
        this.blobShadow(root, r * 0.8);
        this.box(root, M.hull, r * 1.3, r * 0.6, r * 1.5, 0, r * 0.45, 0, false);
        this.box(root, M.dark, r * 0.16, r * 0.16, r * 1.2, -r * 0.35, r * 0.5, r * 0.6, false);
        this.box(root, M.dark, r * 0.16, r * 0.16, r * 1.2, r * 0.35, r * 0.5, r * 0.6, false);
        break;
      case "rocket":
        this.blobShadow(root, r * 0.8);
        this.box(root, M.hull, r * 1.4, r * 0.6, r * 1.3, 0, r * 0.45, 0, false);
        this.box(root, M.dark, r * 1.1, r * 0.5, r * 0.7, 0, r * 0.9, r * 0.2, false);      // pod block
        this.cyl(root, M.accent, r * 0.12, r * 0.6, -r * 0.35, r * 0.9, r * 0.6, 90, 0, false);
        this.cyl(root, M.accent, r * 0.12, r * 0.6, r * 0.35, r * 0.9, r * 0.6, 90, 0, false);
        break;
      case "aa":
        this.blobShadow(root, r * 0.8);
        this.box(root, M.hull, r * 1.4, r * 0.6, r * 1.4, 0, r * 0.45, 0, false);
        this.box(root, M.dark, r * 0.16, r * 0.16, r * 1.2, -r * 0.3, r * 0.9, r * 0.4, false); // angled barrels
        this.box(root, M.dark, r * 0.16, r * 0.16, r * 1.2, r * 0.3, r * 0.9, r * 0.4, false);
        break;
      case "sniper":
        this.blobShadow(root, r * 0.75);
        this.box(root, M.hull, r * 1.2, r * 0.5, r * 1.1, 0, r * 0.4, 0, false);
        this.box(root, M.accent, r * 0.1, r * 0.1, r * 2.0, 0, r * 0.55, r * 0.7, false);   // long thin barrel
        break;
      case "artillery":
        this.blobShadow(root, r * 0.85);
        this.box(root, M.hull, r * 1.5, r * 0.6, r * 1.3, 0, r * 0.4, 0, false);
        this.cyl(root, M.dark, r * 0.45, r * 0.9, 0, r * 0.9, r * 0.1, 20, 0, false);        // raised mortar
        this.cyl(root, M.accent, r * 0.28, r * 0.2, 0, r * 1.4, r * 0.35, 20, 0, false);
        break;
      case "bomb":
        this.blobShadow(root, r * 0.6);
        this.sphere(root, M.hull, r * 0.7, 0, r * 0.6, 0, false);
        this.sphere(root, this.amber, r * 0.32, 0, r * 0.6, 0, false);                       // hazard core
        break;
      default:
        this.blobShadow(root, r * 0.8);
        this.box(root, M.hull, r * 1.4, r * 0.8, r * 1.4, 0, r * 0.5, 0, false);
        break;
    }
    return root;
  }

  /** A dark, sunken wreckage lump reusing the entity footprint. */
  buildWreck(r: number): pc.Entity {
    const root = new pc.Entity("tpl_wreck");
    this.box(root, this.wreck, r * 1.4, r * 0.45, r * 1.2, 0, r * 0.18, 0);
    this.box(root, this.wreck, r * 0.7, r * 0.5, r * 0.8, r * 0.4, r * 0.3, r * 0.2);
    return root;
  }
}
